import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Send, Loader2, ChevronDown, Check } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { PhoneInput } from "@/components/PhoneInput";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

const SERVICE_OPTIONS = [
  "Ecommerce",
  "Shopify",
  "Webflow",
  "WordPress",
  "WooCommerce",
  "AI Solutions",
  "Website Design",
  "SEO & Marketing",
  "Notion Setup",
  "Other",
];

const CONTACT_FORM_ID = "88da5b5e-ab60-4523-a8b1-c6a180dc6880";

function ServicesDropdown({
  selected,
  onChange,
  placeholder,
  countLabel,
}: {
  selected: string[];
  onChange: (services: string[]) => void;
  placeholder: string;
  countLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (service: string) => {
    onChange(
      selected.includes(service)
        ? selected.filter((s) => s !== service)
        : [...selected, service]
    );
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "flex items-center justify-between w-full h-12 px-4 rounded-md border border-input bg-background text-base",
          "hover:border-ring/50 transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          selected.length === 0 && "text-muted-foreground"
        )}
      >
        <span className="truncate">
          {selected.length === 0
            ? placeholder
            : selected.length <= 2
              ? selected.join(", ")
              : `${selected.length} ${countLabel}`}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground shrink-0 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 w-full rounded-lg border border-border bg-background shadow-lg animate-in fade-in-0 zoom-in-95 duration-150">
          <div className="max-h-56 overflow-y-auto py-1">
            {SERVICE_OPTIONS.map((service) => {
              const isSelected = selected.includes(service);
              return (
                <button
                  key={service}
                  type="button"
                  onClick={() => toggle(service)}
                  className={cn(
                    "flex items-center gap-2.5 w-full px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors text-left",
                    isSelected && "bg-primary/5 text-primary"
                  )}
                >
                  <span
                    className={cn(
                      "flex items-center justify-center w-4 h-4 rounded border shrink-0 transition-colors",
                      isSelected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-input"
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span>{service}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const Contact = () => {
  const { lang } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const { canonical, hreflang } = buildHreflang("/contact");
  useCmsPageSEO({
    title: lang === "ar" ? "تواصل مع وافاي | وكالة ويب في حولي، الكويت" : "Contact Wavai | Web Agency in Hawally, Kuwait",
    description: lang === "ar"
      ? "تحدّث مع وافاي، وكالة الكويت المتميّزة للويب والتجارة الإلكترونية. مقرّنا في حولي — نخدم العملاء في الكويت ودول الخليج وحول العالم."
      : "Talk to Wavai, Kuwait's premium web and eCommerce agency. Based in Hawally — serving clients across Kuwait, the GCC and worldwide.",
    keywords: "contact Wavai Kuwait, web agency Hawally, Kuwait digital agency, free consultation",
    canonical,
    hreflang,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Wavai Technology LLC",
      url: "https://www.wavai.ae",
      telephone: "+96597558522",
      email: "hello@wavai.com",
      address: [
        { "@type": "PostalAddress", addressLocality: "Hawally", addressCountry: "KW", streetAddress: "ACE Hardware Building, 2nd Floor, Tunis Street" },
      ],
    },
  });

  const contactInfo = [
    {
      icon: Mail,
      label: t("Email"),
      value: "hello@wavai.com",
      href: "mailto:hello@wavai.com",
    },
    {
      icon: Phone,
      label: t("Phone"),
      value: "+965 97558522",
      href: "tel:+96597558522",
    },
  ];

  const locations = [
    {
      flag: "🇰🇼",
      country: t("Kuwait"),
      line1: t("ACE Hardware Building, 2nd Floor"),
      line2: t("Tunis Street, Hawally, Kuwait"),
    },
    {
      flag: "🇦🇪",
      country: t("UAE"),
      line1: t("Sharjah Media City (Shams)"),
      line2: t("Sharjah, United Arab Emirates"),
    },
  ];

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: [] as string[],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const { error } = await supabase.from("form_submissions").insert({
      form_id: CONTACT_FORM_ID,
      data_json: {
        "What's your name?": formData.name,
        "What's your email address?": formData.email,
        "Phone": formData.phone,
        "Services Needed": formData.services.join(", "),
        "Message": formData.message,
      },
      source_url: window.location.href,
    });

    setSubmitting(false);
    if (error) {
      toast.error(t("Something went wrong. Please try again."));
    } else {
      toast.success(t("Thank you! We'll be in touch soon."));
      setFormData({ name: "", email: "", phone: "", services: [], message: "" });
      supabase.functions.invoke("notify-form-submission", {
        body: {
          submission_data: {
            "What's your name?": formData.name,
            "What's your email address?": formData.email,
            "Phone": formData.phone,
            "Services Needed": formData.services.join(", "),
            "Message": formData.message,
          },
          form_title: "Contact Form",
        },
      }).catch((err) => console.warn("Notification failed:", err));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-24 relative">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <div className="container px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">
                {t("Contact Us")}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
                {t("Let's Build Something")}
                <span className="text-gradient"> {t("in Kuwait 💬")}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t("Ready to take your business to the next level? Our Kuwait team replies within one business day.")}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-12 lg:py-32">
          <div className="container px-6">
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Form */}
              <div className="order-2 lg:order-1">
                <div className="p-8 sm:p-10 rounded-2xl bg-card border border-border shadow-card">
                  <h2 className="text-2xl font-display font-bold mb-8">{t("Start a Project")}</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium mb-2">{t("Name")}</label>
                        <Input
                          placeholder={t("Your name")}
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="h-12 text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">{t("Email")}</label>
                        <Input
                          type="email"
                          placeholder={t("you@company.com")}
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          className="h-12 text-base"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium mb-2">{t("Phone Number")}</label>
                        <PhoneInput
                          value={formData.phone}
                          onChange={(val) => setFormData({ ...formData, phone: val })}
                          placeholder="5XX XXX XXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">{t("Services Needed")}</label>
                        <ServicesDropdown
                          selected={formData.services}
                          onChange={(services) => setFormData({ ...formData, services })}
                          placeholder={t("Select services...")}
                          countLabel={t("services selected")}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">{t("Message")}</label>
                      <Textarea
                        placeholder={t("Tell us about your project...")}
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        className="text-base"
                      />
                    </div>

                    <Button type="submit" variant="hero" className="w-full h-12 text-base" disabled={submitting}>
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {submitting ? t("Sending...") : t("Send Message")}
                      {!submitting && <Send className="h-4 w-4 ml-2" />}
                    </Button>
                  </form>
                </div>
              </div>

              {/* Contact Info */}
              <div className="order-1 lg:order-2">
                <h2 className="text-2xl font-display font-bold mb-6">{t("Get in Touch")}</h2>
                <p className="text-muted-foreground mb-8">
                  {t("Need help with a project? Our Kuwait team is on standby — expect a reply within one business day. Have something urgent? Call our Kuwait office and a real human will pick up.")}
                </p>

                <div className="space-y-4">
                  {contactInfo.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="flex items-start gap-4 p-5 hover:bg-muted/30 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <item.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{item.label}</p>
                        <p className="font-medium" dir="ltr">{item.value}</p>
                      </div>
                    </a>
                  ))}

                  {/* Locations */}
                  <div className="grid grid-cols-2 gap-3">
                    {locations.map((loc) => (
                      <div
                        key={loc.country}
                        className="p-5"
                      >
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <MapPin className="h-4 w-4 text-primary" />
                          </div>
                          <span className="text-lg">{loc.flag}</span>
                          <p className="font-semibold text-sm">{loc.country}</p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {loc.line1}
                          <br />
                          {loc.line2}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Book a Call Section */}
        <section className="py-12 lg:py-32 border-t border-border">
          <div className="container px-6">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <h2 className="text-3xl font-display font-bold mb-3">
                {t("Book a")} <span className="text-gradient">{t("Call")}</span>
              </h2>
              <p className="text-muted-foreground">
                {t("Schedule a quick call with our team to discuss your project.")}
              </p>
            </div>
            <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden bg-card border border-border shadow-card">
              <iframe
                src="https://cal.com/wavai/30min?embed=true&theme=light&hideEventTypeDetails=false"
                className="w-full border-0"
                style={{ height: "660px" }}
                title={t("Book a Call")}
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
